import type { Zone } from './zones';

export type ArchetypeId =
  | 'DIVER'
  | 'HIGH_WALL'
  | 'LEFT_LEANER'
  | 'CENTER_SPREAD'
  | 'WIDE_OPEN'
  | 'TWO_CLUSTER';

export type ZoneDistribution = Record<Zone, number>;

export interface Archetype {
  id: ArchetypeId;
  /** Display string keys for i18n. */
  displayKey: string;
  distribution: ZoneDistribution;
}

/**
 * The 6 daily keeper archetypes. All distributions sum to 1.0.
 * See spec §2.3 for design rationale and per-archetype expected scores.
 */
export const ARCHETYPES: readonly Archetype[] = [
  {
    id: 'DIVER',
    displayKey: 'archetype.diver',
    distribution: { LF: 0.05, CF: 0.05, RF: 0.05, LA: 0.4, CA: 0.05, RA: 0.4 },
  },
  {
    id: 'HIGH_WALL',
    displayKey: 'archetype.highWall',
    distribution: { LF: 0.07, CF: 0.06, RF: 0.07, LA: 0.25, CA: 0.3, RA: 0.25 },
  },
  {
    id: 'LEFT_LEANER',
    displayKey: 'archetype.leftLeaner',
    distribution: { LF: 0.32, CF: 0.07, RF: 0.06, LA: 0.32, CA: 0.07, RA: 0.16 },
  },
  {
    id: 'CENTER_SPREAD',
    displayKey: 'archetype.centerSpread',
    distribution: { LF: 0.15, CF: 0.2, RF: 0.15, LA: 0.15, CA: 0.2, RA: 0.15 },
  },
  {
    id: 'WIDE_OPEN',
    displayKey: 'archetype.wideOpen',
    distribution: { LF: 1 / 6, CF: 1 / 6, RF: 1 / 6, LA: 1 / 6, CA: 1 / 6, RA: 1 / 6 },
  },
  {
    id: 'TWO_CLUSTER',
    displayKey: 'archetype.twoCluster',
    distribution: { LF: 0.35, CF: 0.075, RF: 0.075, LA: 0.075, CA: 0.075, RA: 0.35 },
  },
];

/**
 * Difficulty tier (1=easy, 2=medium, 3=hard) derived from Shannon entropy
 * of the distribution. Higher entropy → flatter distribution → no exploitable
 * "safe" zones → harder for the player. Pure function of `distribution`,
 * so adjusting an archetype's numbers automatically retiers it.
 */
export function archetypeTier(a: Archetype): 1 | 2 | 3 {
  const ps = Object.values(a.distribution).filter((p) => p > 0);
  const entropy = -ps.reduce((s, p) => s + p * Math.log2(p), 0);
  if (entropy < 2.2) return 1;
  if (entropy < 2.5) return 2;
  return 3;
}
